/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { Component } from 'react';
import {
    bool, func, string,
    object, arrayOf, shape, number,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import mbpLogger from '@module-logger/mbp';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import EventDispatcher from '../../../state/reducers/EventDispatcher';
import configENV from '../../../config';
import { getBrand, getCustomerEmail } from '../../../state/ducks/Common/Common-Selectors';
import { getFeatureFlags } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import AccountLeftMenu from '../../../common/Desktop/AccountLeftMenu';
import SubscriptionList from '../MobileSubscriptions/SubscriptionList';
import { isEmpty } from '../../../utils/object';

const { PERSIST_KEY } = configENV;

const styles = () => ({
    defaultBody: {
        color: '#666',
        maxWidth: '86%',
        '@media screen and (min-width: 1440px)': {
            maxWidth: '92%',
        },
    },
    header: {
        marginBottom: '19px',
        height: '29px',
        '& h1': {
            marginTop: '0',
        },
    },
    container: {
        maxWidth: '1440px',
        margin: 'auto',
        paddingBottom: '20px',
    },
});

class DesktopSubscriptions extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            subscriptionsList: [],
            page: 0,
        };

        const {
            customerId, fetchSubscriptions, brand, subscriptions, featureFlags,
        } = props;
        const { page } = this.state;
        if (isEmpty(subscriptions)) fetchSubscriptions({ customerId, page }, brand, subscriptions, featureFlags);  // fetch subscriptions into newState
    }

    static getDerivedStateFromError(/* error */) {
        return { hasError: true };
    }

    componentDidMount = () => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    componentDidCatch = (jsError, reactInfo) => {
        mbpLogger.logError({
            message: `Unhandled Error in DesktopSubscriptions \n jsError = ${jsError} \n reactInfo = ${reactInfo}`,
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
        });
    }

    handleCancelSubscription = (subscriptionRuleId) => {
        const { cancelSubscription, brand, featureFlags } = this.props;
        cancelSubscription(subscriptionRuleId, brand, featureFlags);
    }

    handleUpdateSubscription = (updateType, subscriptionRuleId) => {
        const {
            updateSubscription, brand, featureFlags,
        } = this.props;
        updateSubscription(subscriptionRuleId, brand, featureFlags, updateType);
    }

    hanldeLoadMore = () => {
        const {
            customerId, fetchSubscriptions, brand, subscriptions, featureFlags,
        } = this.props;
        const { page } = this.state;
        this.setState({ page: page + 1 }, () => fetchSubscriptions({ customerId, page: page + 1 }, brand, subscriptions, featureFlags));
    }

    render() {
        const { error, hasError, subscriptionsList } = this.state;
        const {
            isFetchingModal, classes, userProfile, featureFlags, customerId,
        } = this.props;

        if (error || hasError) {
            mbpLogger.logError({
                message: `Unable to fetch user ${customerId} subscriptions due to: ${error}`,
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
            });
            return <div>Sorry! - we were unable to access your subscriptions.<br />Please try again later.</div>;
        }

        return (
            <div className={classes.container}>
                <Grid className="container" container>
                    <AccountLeftMenu
                        userProfile={userProfile}
                        featureFlags={featureFlags}
                    />
                    <Grid item xs={10} style={{ padding: '40px 11px 0 43px' }}>
                        <div className={classes.defaultBody}>
                            <div className={classes.header}><h1>Manage Subscriptions</h1></div>
                            <SubscriptionList
                                subscriptions={subscriptionsList}
                                featureFlags={featureFlags}
                                handleCancel={this.handleCancelSubscription}
                                handleUpdate={this.handleUpdateSubscription}
                                hanldeLoadMore={this.hanldeLoadMore}
                                isFetchingModal={isFetchingModal}
                            />
                        </div>
                    </Grid>
                </Grid>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    return {
        isFetchingModal: newState.isFetchingModal,
        subscriptionResMsg: newState.subscriptionResMsg,
        brand: getBrand(state),
        customerId: getCustomerEmail(state),
        subscriptions: newState.subscriptions,
        featureFlags: getFeatureFlags(state),
    };
};

DesktopSubscriptions.defaultProps = {
    fetchSubscriptions: () => { },
    cancelSubscription: () => { },
    updateSubscription: () => { },
    subscriptions: [],
    userProfile: {},
    featureFlags: {},
};

DesktopSubscriptions.propTypes = {
    customerId: string.isRequired,
    isFetchingModal: bool.isRequired,
    fetchSubscriptions: func,
    cancelSubscription: func,
    updateSubscription: func,
    brand: object.isRequired,
    subscriptions: arrayOf(
        shape({
            checkoutBrand: string,
            duration: number.isRequired,
            endDate: string.isRequired,
            interval: number.isRequired,
            nextOrderDate: string,
            orderDate: string.isRequired,
            orderHistoryResult: arrayOf(
                shape({
                    deliveryDate: string,
                    discountAmount: string,
                    orderDate: string.isRequired,
                    orderNumber: string.isRequired,
                    productCode: string.isRequired,
                    productName: string.isRequired,
                    productPrice: string.isRequired,
                    quantity: string.isRequired,
                }),
            ),
            orderNumber: string.isRequired,
            orderTime: string.isRequired,
            orderType: string.isRequired,
            price: string.isRequired,
            productId: string.isRequired,
            productImageCategory: string.isRequired,
            productImageThumbnail: string.isRequired,
            productName: string.isRequired,
            quantity: string,
            recipient: arrayOf(shape({
                city: string.isRequired,
                country: string.isRequired,
                firstName: string.isRequired,
                lastName: string.isRequired,
                state: string.isRequired,
                street1: string.isRequired,
                street2: string.isRequired,
                zipcode: string.isRequired,
            })),
            ruleId: string.isRequired,
            startDate: string.isRequired,
            subscriptionStatus: string.isRequired,
            totalAmount: string.isRequired,
        }),
    ),
    classes: object.isRequired,
    userProfile: shape({
        userRole: string.isRequired,
        email: string.isRequired,
        XAtt4: string.isRequired,
        XAtt5: string.isRequired,
    }),
    featureFlags: object,
};

const mapDispatchToProps = (dispatch) => ({
    fetchSubscriptions: (customerId, brand, subscriptions, featureFlags) => dispatch(EventDispatcher.fetchSubscriptions(customerId, brand, subscriptions, featureFlags)),
    cancelSubscription: (ruleId, brand, featureFlags) => dispatch(EventDispatcher.cancelSubscription(ruleId, brand, featureFlags)),
    updateSubscription: (ruleId, brand, featureFlags, updateType) => dispatch(EventDispatcher.updateSubscription(ruleId, brand, featureFlags, updateType)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(withStyles(styles, { withTheme: true })(DesktopSubscriptions)));
